import React, { useMemo, useState } from 'react';
import { Alert, Card, Empty, Select, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { useGetOrganizationActivityQuery } from '../../services/nestjs-base-api';

const { Text, Title } = Typography;

interface OrganizationActivityTabProps {
    jwtToken: string;
    organisationId: number;
}

interface ActivityEvent {
    timestamp: string;
    event: string;
    path: string | null;
    elementText: string | null;
    userEmail: string | null;
    source: 'posthog' | 'platform';
    detail?: string | null;
}

/** A gap longer than this starts a new session block. */
const SESSION_GAP_MINUTES = 30;

/** Noise that adds nothing to a "what did they do" read. */
const HIDDEN_EVENTS = new Set(['$web_vitals', '$set', '$feature_flag_called']);

const EVENT_COLORS: Record<string, string> = {
    $pageview: 'blue',
    $pageleave: 'default',
    $autocapture: 'geekblue',
    $identify: 'purple',
    'User Login': 'green',
    'User Registered': 'gold',
    'Organisation Created': 'gold',
    'Password Reset Requested': 'orange',
    // Email lifecycle — the half PostHog can never see, since it happens in
    // the recipient's mail client.
    CRM_EMAIL_SENT: 'cyan',
    EMAIL_DELIVERED: 'cyan',
    EMAIL_OPENED: 'green',
    EMAIL_CLICKED: 'magenta',
    EMAIL_BOUNCED: 'red',
    EMAIL_COMPLAINT_RECEIVED: 'red',
};

/** Email rows are the point of merging the two sources — make them stand out. */
const isEmailEvent = (event: string): boolean =>
    event.startsWith('EMAIL_') || event === 'CRM_EMAIL_SENT';

/** Friendlier label than the raw event name, from either source. */
const eventLabel = (event: string): string => {
    if (event === '$autocapture') return 'click';
    if (event === '$pageview') return 'page';
    if (event === '$pageleave') return 'left';
    if (event === '$identify') return 'identify';
    // Platform events are SCREAMING_SNAKE; make them readable.
    if (/^[A-Z][A-Z0-9_]+$/.test(event)) {
        return event.toLowerCase().replace(/_/g, ' ');
    }
    return event;
};

export const OrganizationActivityTab: React.FC<OrganizationActivityTabProps> = ({
    jwtToken,
    organisationId,
}) => {
    const intl = useIntl();
    const [days, setDays] = useState(7);

    const { data, isFetching } = useGetOrganizationActivityQuery({
        jwtToken,
        organisationId,
        days,
    });

    // Split the flat event stream into session blocks on idle gaps, so the
    // timeline reads as "visits" rather than one undifferentiated list.
    const sessions = useMemo(() => {
        const events = (data?.events || []).filter(
            (e: ActivityEvent) => !HIDDEN_EVENTS.has(e.event),
        );
        const blocks: ActivityEvent[][] = [];
        let current: ActivityEvent[] = [];
        let previous: dayjs.Dayjs | null = null;

        events.forEach((e: ActivityEvent) => {
            const at = dayjs(e.timestamp);
            if (previous && at.diff(previous, 'minute') > SESSION_GAP_MINUTES) {
                blocks.push(current);
                current = [];
            }
            current.push(e);
            previous = at;
        });
        if (current.length) blocks.push(current);
        return blocks.reverse(); // most recent visit first
    }, [data]);

    return (
        <div>
            {/* Platform event-log rows still render without PostHog, so warn
                about the missing half rather than replacing the whole tab. */}
            {data && data.enabled === false && (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={<FormattedMessage id="admin.activity.disabled" defaultMessage="In-app clickstream not configured" />}
                    description={<FormattedMessage id="admin.activity.disabledHelp" defaultMessage="Showing platform events only. Set POSTHOG_PERSONAL_API_KEY and POSTHOG_PROJECT_ID to also read in-app activity back from PostHog." />}
                />
            )}
            <Space style={{ marginBottom: 16 }}>
                <Text type="secondary">
                    <FormattedMessage id="admin.activity.period" defaultMessage="Period" />
                </Text>
                <Select
                    value={days}
                    style={{ width: 160 }}
                    onChange={setDays}
                    options={[1, 7, 30, 90].map((d) => ({
                        value: d,
                        label: intl.formatMessage({ id: 'admin.activity.lastDays' }, { days: d }),
                    }))}
                />
                {isFetching && <Spin size="small" />}
            </Space>

            {!isFetching && sessions.length === 0 && (
                <Empty
                    description={intl.formatMessage({ id: 'admin.activity.empty' })}
                />
            )}

            {sessions.map((session, i) => {
                const start = dayjs(session[0].timestamp);
                const end = dayjs(session[session.length - 1].timestamp);
                const durationMins = Math.max(0, end.diff(start, 'minute'));
                return (
                    <Card
                        key={i}
                        size="small"
                        style={{ marginBottom: 16 }}
                        title={
                            <Space wrap>
                                <Title level={5} style={{ margin: 0 }}>
                                    {start.format('D MMM YYYY, HH:mm')}
                                </Title>
                                <Text type="secondary">
                                    {durationMins >= 1
                                        ? intl.formatMessage({ id: 'admin.activity.duration' }, { mins: durationMins })
                                        : intl.formatMessage({ id: 'admin.activity.underAMinute' })}
                                </Text>
                                <Tag>{intl.formatMessage({ id: 'admin.activity.eventCount' }, { count: session.length })}</Tag>
                                {session[0].userEmail && <Tag color="blue">{session[0].userEmail}</Tag>}
                            </Space>
                        }
                    >
                        {session.map((e, j) => (
                            <div
                                key={j}
                                style={{
                                    display: 'flex',
                                    gap: 12,
                                    alignItems: 'baseline',
                                    padding: '4px 6px',
                                    borderBottom: j < session.length - 1 ? '1px solid #fafafa' : 'none',
                                    fontSize: 13,
                                    // Tint email rows so the delivered → opened →
                                    // clicked chain is findable at a glance.
                                    background: isEmailEvent(e.event) ? '#f6ffed' : undefined,
                                    borderRadius: isEmailEvent(e.event) ? 4 : undefined,
                                }}
                            >
                                <Text code style={{ flexShrink: 0 }}>
                                    {dayjs(e.timestamp).format('HH:mm:ss')}
                                </Text>
                                <Tooltip
                                    title={
                                        e.source === 'platform'
                                            ? intl.formatMessage({ id: 'admin.activity.sourcePlatform' })
                                            : intl.formatMessage({ id: 'admin.activity.sourcePosthog' })
                                    }
                                >
                                    <Tag
                                        color={e.source === 'platform' ? 'purple' : 'default'}
                                        style={{ flexShrink: 0, margin: 0, fontSize: 10 }}
                                    >
                                        {e.source === 'platform' ? 'platform' : 'app'}
                                    </Tag>
                                </Tooltip>
                                <Tag color={EVENT_COLORS[e.event] || 'default'} style={{ flexShrink: 0, margin: 0 }}>
                                    {eventLabel(e.event)}
                                </Tag>
                                {e.elementText && (
                                    <Text strong style={{ flexShrink: 0 }}>
                                        “{e.elementText.replace(/\s+/g, ' ').trim().slice(0, 60)}”
                                    </Text>
                                )}
                                {e.detail && (
                                    <Text style={{ flexShrink: 1, minWidth: 0 }} ellipsis>
                                        {e.detail}
                                    </Text>
                                )}
                                <Text type="secondary" ellipsis style={{ marginLeft: 'auto' }}>
                                    {e.path}
                                </Text>
                            </div>
                        ))}
                    </Card>
                );
            })}
        </div>
    );
};
